import { Link } from "react-router-dom";
import type { StatusTone } from "../lib/overview-ops";
import {
  relatedSiteLinks,
  siteCodeLockLabelFa,
  siteCodeLockTone,
  siteGroupLabelFa,
  siteInventoryTitleMeta,
  siteLifecycleLabelFa,
  siteLifecycleTone,
  siteResellerLabelFa,
  type SiteInventoryRow,
  type SiteSortKey,
  type SiteStatusFilter,
} from "../lib/sites-ops";
import { cmsStrings } from "../terminology";
import { FilterToolbar } from "./FilterToolbar";
import { LtrIsolate } from "./LtrIsolate";

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

type FilterBarProps = {
  query: string;
  onQueryChange: (value: string) => void;
  status: SiteStatusFilter;
  onStatusChange: (value: SiteStatusFilter) => void;
  sort: SiteSortKey;
  onSortChange: (value: SiteSortKey) => void;
  showStatusFilter: boolean;
};

export function SitesFilterBar({
  query,
  onQueryChange,
  status,
  onStatusChange,
  sort,
  onSortChange,
  showStatusFilter,
}: FilterBarProps) {
  const ui = cmsStrings.sitesUi;

  return (
    <FilterToolbar
      className="list-filter-bar sites-filter-bar announcements-filter-bar"
      aria-label={ui.searchAria}
    >
      {showStatusFilter ? (
        <div
          className="filter-toolbar__chips announcements-filter-bar__chips"
          role="group"
          aria-label={ui.statusAria}
        >
          {(
            [
              ["all", ui.filterAll],
              ["active", ui.filterActive],
              ["inactive", ui.filterInactive],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`filter-chip${status === value ? " filter-chip--active" : ""}`}
              onClick={() => onStatusChange(value)}
              aria-pressed={status === value}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      <label className="sites-filter-bar__field announcements-filter-bar__field">
        <span className="visually-hidden">{ui.searchHidden}</span>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={ui.searchPlaceholder}
          aria-label={ui.searchAria}
        />
      </label>
      <label className="sites-filter-bar__field announcements-filter-bar__field">
        <span>{cmsStrings.inventory.sortLabel}</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as SiteSortKey)}
          aria-label={ui.sortAria}
        >
          <option value="name">{ui.colName}</option>
          <option value="code">{ui.colSiteCode}</option>
        </select>
      </label>
    </FilterToolbar>
  );
}

type InspectProps = {
  site: SiteInventoryRow;
  canReadResellers: boolean;
};

export function SiteInspectSummary({ site, canReadResellers }: InspectProps) {
  const ui = cmsStrings.sitesUi;
  const links = relatedSiteLinks().filter((l) => canReadResellers || l.id !== "reseller");

  return (
    <section
      className="content-inspect content-inspect--site"
      aria-labelledby="site-inspect-title"
    >
      <header className="content-inspect__hero">
        <div className="content-inspect__hero-row">
          <h2 id="site-inspect-title" className="content-inspect__name">
            {site.name}
          </h2>
          <div className="content-inspect__badges" aria-label={ui.statusAria}>
            <span className={`state-badge ${TONE_BADGE[siteLifecycleTone(site.active)]}`}>
              {siteLifecycleLabelFa(site.active)}
            </span>
            {site.codeLocked !== undefined ? (
              <span className={`state-badge ${TONE_BADGE[siteCodeLockTone(site.codeLocked)]}`}>
                کد {siteCodeLockLabelFa(site.codeLocked)}
              </span>
            ) : null}
          </div>
        </div>
        <p className="content-inspect__code">
          <span className="content-inspect__code-label">{cmsStrings.technical.SITE_CODE}</span>
          <LtrIsolate mono>{site.code}</LtrIsolate>
        </p>
        {site.location?.trim() ? (
          <p className="content-inspect__inline-fact">{site.location}</p>
        ) : null}
      </header>

      <div className="content-inspect__block">
        <h3 className="content-inspect__block-title">{ui.inspectSectionMembership}</h3>
        <dl className="content-inspect__facts content-inspect__facts--pair">
          <div>
            <dt>{ui.factGroup}</dt>
            <dd>{siteGroupLabelFa(site.groupName)}</dd>
          </div>
          {canReadResellers ? (
            <div>
              <dt>{ui.factReseller}</dt>
              <dd>{siteResellerLabelFa(site.resellerLabel)}</dd>
            </div>
          ) : null}
        </dl>
      </div>

      <nav className="content-inspect__text-nav" aria-label={ui.inspectSectionRelease}>
        {links.map((link) => (
          <Link key={link.id} to={link.to} className="content-inspect__text-link">
            {link.label}
          </Link>
        ))}
      </nav>
    </section>
  );
}

export type SiteGroupRow = {
  id: string;
  name: string;
  description: string;
  active: boolean;
  memberCount: number;
  memberCodes: string[];
};

type GroupsListProps = {
  rows: SiteGroupRow[];
  canEdit: boolean;
  onEdit: (id: string) => void;
  editLabel: string;
  membersUnit: string;
  notesPrefix?: string;
};

export function SiteGroupsRoster({ rows, canEdit, onEdit, editLabel, membersUnit }: GroupsListProps) {
  const ui = cmsStrings.sitesUi;

  return (
    <ul className="sites-activity-list" aria-label={ui.groupsCardsAria}>
      {rows.map((row) => (
        <li key={row.id} className="sites-activity-list__item">
          <div className="sites-activity-list__body">
            <strong className="sites-activity-list__name">{row.name}</strong>
            <p className="sites-activity-list__meta">
              <LtrIsolate mono>{row.memberCount}</LtrIsolate> {membersUnit}
              {row.description?.trim() ? ` · ${row.description}` : ""}
            </p>
          </div>
          {canEdit ? (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => onEdit(row.id)}>
              {editLabel}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

type InventoryProps = {
  rows: SiteInventoryRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  canManage: boolean;
  onEdit?: (row: SiteInventoryRow) => void;
  onDeactivate?: (row: SiteInventoryRow) => void;
};

export function SitesInventoryList({
  rows,
  selectedId,
  onSelect,
  canManage,
  onEdit,
  onDeactivate,
}: InventoryProps) {
  const ui = cmsStrings.sitesUi;

  return (
    <>
      <div
        className="sites-inventory-table-wrap inventory-table-wrap"
        role="region"
        aria-label={ui.tableAria}
      >
        {/* Desktop row opens جزئیات inspect via title select */}
        <table className="data-table sites-inventory-table content-inventory-table inventory-table">
          <thead>
            <tr>
              <th scope="col">{ui.colName}</th>
              <th scope="col">{ui.colStatus}</th>
              <th scope="col">{ui.colGroup}</th>
              <th scope="col">{ui.colSiteCode}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selected = row.id === selectedId;
              const meta = siteInventoryTitleMeta(row);
              return (
                <tr
                  key={row.id}
                  className={selected ? "sites-inventory-table__row--selected" : undefined}
                  data-selected={selected ? "true" : undefined}
                >
                  <td>
                    <button
                      type="button"
                      className="sites-inventory__select-btn"
                      onClick={() => onSelect(row.id)}
                      aria-pressed={selected}
                      aria-label={`${cmsStrings.inventory.details}: ${row.name}`}
                    >
                      {row.name}
                    </button>
                    {meta ? (
                      <div className="sites-inventory__muted inventory-cell-truncate" title={meta}>
                        {meta}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <span className={`state-badge ${TONE_BADGE[siteLifecycleTone(row.active)]}`}>
                      {siteLifecycleLabelFa(row.active)}
                    </span>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={siteGroupLabelFa(row.groupName)}>
                      {siteGroupLabelFa(row.groupName)}
                    </div>
                  </td>
                  <td>
                    <div className="inventory-cell-truncate" title={row.code}>
                      <LtrIsolate mono>{row.code}</LtrIsolate>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ul
        className="sites-inventory-cards content-inventory-cards inventory-cards"
        aria-label={ui.cardsAria}
      >
        {rows.map((row) => {
          const selected = row.id === selectedId;
          const meta = siteInventoryTitleMeta(row);
          return (
            <li
              key={row.id}
              className={`sites-inventory-card inventory-card${selected ? " sites-inventory-card--selected" : ""}`}
            >
              <button
                type="button"
                className="sites-inventory-card__hit"
                onClick={() => onSelect(row.id)}
                aria-pressed={selected}
                aria-label={`${cmsStrings.inventory.details}: ${row.name}`}
              >
                <span className="sites-inventory-card__title">{row.name}</span>
                <span className="sites-inventory-card__code">
                  <LtrIsolate mono>{row.code}</LtrIsolate>
                </span>
                <span className="sites-inventory-card__badges">
                  <span className={`state-badge ${TONE_BADGE[siteLifecycleTone(row.active)]}`}>
                    {siteLifecycleLabelFa(row.active)}
                  </span>
                  {row.codeLocked !== undefined ? (
                    <span className={`state-badge ${TONE_BADGE[siteCodeLockTone(row.codeLocked)]}`}>
                      کد {siteCodeLockLabelFa(row.codeLocked)}
                    </span>
                  ) : null}
                </span>
                <span className="sites-inventory__muted">
                  {ui.colGroup}: {siteGroupLabelFa(row.groupName)}
                </span>
                {meta ? <span className="sites-inventory__muted">{meta}</span> : null}
              </button>
              {canManage ? (
                <div className="sites-inventory-card__actions">
                  {onEdit ? (
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => onEdit(row)}>
                      {cmsStrings.sitesComposer.edit}
                    </button>
                  ) : null}
                  {row.active !== false && onDeactivate ? (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => onDeactivate(row)}
                    >
                      {cmsStrings.sitesComposer.deactivate}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
}
